<script setup>
import DocumentSlot from '../files/DocumentSlot.vue';
import DocumentGroup from '../files/DocumentGroup.vue';
import VisaList from '../files/VisaList.vue';
import InvitationList from '../files/InvitationList.vue';
import ContractList from '../files/ContractList.vue';
import { useAuth } from '../../composables/useAuth.js';
import { canReadIdentityFiles, canReadTravelSummary } from '../../domain/permissions.js';

defineProps({
  player: { type: Object, required: true },
});

const { can, role, hidesInaccessibleAreas } = useAuth();

/* Feste Ausweisdokumente — je Spieler genau einer pro Art.
   Geburtsurkunde und Führungszeugnis bewusst nicht mehr hier (Entscheidung
   Bastian, TODO "Dokumentengruppe Identity: Police Clearance und Birth
   Certificate raus", Notizen 2026-09-23) — gehören nicht mehr zu den
   erwarteten Identitätsdokumenten. Bereits hochgeladene Dateien bleiben in
   player.identityDocs.birthCert/.policeClearance und im Storage-Bucket
   liegen, sind aber nirgends mehr in der Oberfläche zu sehen. */
const IDENTITY_DOCS = [
  { id: 'passport', label: 'documents.identity.passport' },
];
</script>

<template>
  <div>
    <div class="doc-group">
      <h3>{{ $t('documents.identityTitle') }}</h3>
      <!-- Scans der Ausweisdokumente: nicht für staff (Speicherregel identity/, Entscheidung 2026-10-15) -->
      <div v-if="!canReadIdentityFiles(role)" class="locked-group">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="4" y="10.5" width="16" height="10" rx="2" />
          <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" stroke-linecap="round" />
        </svg>
        <span>{{ $t('sector.lockedGroup') }}</span>
      </div>
      <DocumentSlot
        v-for="item in canReadIdentityFiles(role) ? IDENTITY_DOCS : []"
        :key="item.id"
        :player="player"
        :slot-id="item.id"
        :label="$t(item.label)"
      />
    </div>

    <!-- Visa und Einladungen: admin, board, data voll (Bereich travel); staff seit F7 nur lesend und nur Art, Land, Status,
         Termine (die Ansicht players_staff liefert nur diese Felder; Bearbeiten, Dateien und Löschen hängen an can('travel','write')). -->
    <VisaList v-if="canReadTravelSummary(role)" :player="player" />

    <InvitationList v-if="canReadTravelSummary(role)" :player="player" />

    <DocumentGroup
      :player="player"
      group-key="federationDocs"
      :title="$t('documents.federation')"
      :hint="$t('documents.federationHint')"
      :label-placeholder="$t('documents.federationPlaceholder')"
    />

    <!-- Verträge sind kaufmännische Daten: nur Admin und Vorstand.
         Siehe docs/rollen-konzept.md. Ohne Zugriff bleibt die Gruppe sichtbar
         und erklärt sich, außer für Rollen aus HIDE_INACCESSIBLE_AREAS_FOR
         (aktuell `guest`) — siehe domain/permissions.js. -->
    <ContractList v-if="can('contracts')" :player="player" />

    <div v-else-if="!hidesInaccessibleAreas" class="doc-group">
      <h3>{{ $t('documents.contracts') }}</h3>
      <div class="locked-group">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="4" y="10.5" width="16" height="10" rx="2" />
          <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" stroke-linecap="round" />
        </svg>
        <span>{{ $t('sector.lockedGroup') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.locked-group {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px;
  background: var(--card);
  border: 1px dashed var(--border);
  border-radius: 10px;
  color: var(--dim);
  font-size: 0.8rem;
  line-height: 1.5;
}
</style>
